import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CheckCircle2, ShieldCheck } from "lucide-react";
import { IMAGES } from "@/data/site";
import { ONE_STOP, SPACES } from "@/data/spaces";
import { SpacesGrid } from "@/components/home/SpacesGrid";
import { QuickQuote } from "@/components/home/QuickQuote";

const RotatingSpace = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % SPACES.length), 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="relative block h-[2.2em] overflow-hidden sm:h-[1.15em]" data-testid="hero-rotating-space">
      <AnimatePresence mode="wait">
        <motion.span key={i} initial={{ y: "100%", opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: "-100%", opacity: 0 }} transition={{ duration: 0.45 }} className="absolute inset-x-0 top-0 italic text-[#E8927F]">
          {SPACES[i].name}.
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

const OneStopStrip = () => (
  <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2" data-testid="hero-one-stop-services">
    {ONE_STOP.map((s) => (
      <span key={s} className="flex items-center gap-1.5 text-sm font-medium text-slate-200">
        <CheckCircle2 className="h-4 w-4 text-emerald-400" /> {s}
      </span>
    ))}
  </div>
);

export const Hero = () => {
  const [quote, setQuote] = useState({ open: false, space: "" });
  const openQuote = (space = "") => setQuote({ open: true, space });
  return (
    <section className="relative isolate overflow-hidden bg-[#123634]" data-testid="home-hero">
      <div className="absolute inset-0 -z-20 overflow-hidden">
        <img src={IMAGES.hero} alt="Vinsafe engineer inspecting fire safety equipment at an Indian public building" className="animate-kenburns h-full w-full object-cover object-[70%_center] opacity-60" data-testid="home-hero-image" />
      </div>
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#123634] via-[#123634]/85 to-[#123634]/30" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#123634] via-[#123634]/40 to-transparent" />
      <div className="container-x py-20 lg:py-24">
        <div className="max-w-4xl">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-slate-200 backdrop-blur">
            <ShieldCheck className="h-3.5 w-3.5 text-[#E8927F]" /> One-stop fire safety solution for all public spaces
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.7 }} className="mt-6 text-4xl font-extrabold leading-[1.08] text-white sm:text-5xl lg:text-6xl" data-testid="home-hero-title">
            One partner. Fire-safe for every
            <RotatingSpace />
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.7 }} className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">
            From a single clinic to a metro network, Vinsafe supplies, wall-mounts, wires and maintains your fire safety equipment, under one accountable partner.
          </motion.p>
          <OneStopStrip />
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }} className="mt-8 flex flex-col gap-3 sm:flex-row">
            <button type="button" onClick={() => openQuote()} className="btn-primary px-7 py-3.5" data-testid="hero-quick-quote-button">Get a quick quote <ArrowRight className="h-4 w-4" /></button>
            <Link to="/amc" className="inline-flex items-center justify-center rounded-lg border border-white/25 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/15" data-testid="hero-amc-button">Explore AMC plans</Link>
          </motion.div>
        </div>
        <div className="mt-12"><SpacesGrid onPick={openQuote} /></div>
        <div className="mono mt-8 flex flex-wrap gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.18em] text-slate-400">
          <span>NBCS 2026 aligned</span><span>·</span><span>IS / NFPA referenced</span><span>·</span><span>24/7 service desk</span>
        </div>
      </div>
      <QuickQuote open={quote.open} space={quote.space} onOpenChange={(o) => setQuote((q) => ({ ...q, open: o }))} />
    </section>
  );
};
